import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useIdentity } from '../hooks/useIdentity';
import { AuthRoute, InstanceRoute } from './routeGuards';
import { AppLayout } from '../components/AppLayout';
import { TourRoot } from '../components/Tour';
import { useAuth } from '../hooks/useAuth';
import { isAccountSidebarHidden } from './sidebar/identity';
import { TourProvider, useTourContext, useAppearanceTour } from '../hooks/useTourContext';
import { CipherStoreProvider } from '../hooks/useCipherStore';
import { ChatSocketProvider } from '../hooks/useChatSocket';
import { FriendsProvider } from '../hooks/useFriends';
import { InboxProvider } from '../hooks/useInbox';
import { CaptchaGateProvider } from '../components/CaptchaGateProvider';
import { BlockProvider } from '../hooks/useBlockContext';
import { ConversationsProvider } from '../hooks/useConversations';
import { SpacesProvider } from '../hooks/useSpaces';
import { MediaOutboxProvider } from '../services/mediaOutbox';
import { ThreadPopoutProvider } from '../pages/threads/ThreadPopoutProvider';
import { ConversationPreferencesProvider } from '../hooks/useConversationPreferences';
import { SpacePreferencesProvider } from '../hooks/useSpacePreferences';
import { ConversationFoldersProvider } from '../hooks/useConversationFolders';
import { usePreKeys } from '../hooks/usePreKeys';
import { KeyStorageBanner } from '../components/KeyStorageBanner';
import { UpdateAvailableBanner } from '../components/UpdateAvailableBanner';
import { SiteAnnouncementBanner } from '../components/SiteAnnouncementBanner';
import { SiteAnnouncementsProvider } from '../hooks/useSiteAnnouncements';
import { UpdateOverlay } from '../components/UpdateOverlay';
import { AchievementListener } from '../components/AchievementListener';
import { CallPresenceSoundListener } from '../components/call/CallPresenceSoundListener';
import { SubscriptionChangeListener } from '../components/SubscriptionChangeListener';
import { UpdateProvider } from '../hooks/useUpdateContext';
import { useEnsureUpdateCheck } from '../hooks/useEnsureUpdateCheck';
import { ToasterOutlet } from '../components/Toast';
import { IdentityModalProvider } from '../hooks/useIdentityModal';
import { CallSessionProvider } from '../hooks/useCallSession';
import { VoiceChannelSessionProvider } from '../hooks/useVoiceChannelSession';
import { GlobalCallEventsProvider } from '../hooks/useGlobalCallEvents';
import { AppCallOverlay } from '../components/call/AppCallOverlay';
import { VoiceChannelOverlay } from '../components/call/VoiceChannelOverlay';
import { useIncomingCallRinger } from '../hooks/useIncomingCallRinger';
import { AppSidebar } from './AppSidebar';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { SPACE_INVITE_PATH_PREFIX } from '../pages/spaces/spaceInviteUtils';
// Side-effect: migrate legacy Bubble message layout → Linear on app load.
import '../hooks/useMessageLayoutPreference';

// ============================================================================
// Lazily-loaded route pages
// ============================================================================
// Route components are code-split so heavy/rare areas (admin → recharts,
// conversations, moderation, identity, legal) stay out of the initial bundle.
// Each lazy() points at a concrete module or a feature barrel (one chunk per
// feature). All providers/layout/overlays above remain eager so context and
// persistent UI (sidebar, call audio) survive navigation.

function lazyRoute<M, K extends keyof M>(loader: () => Promise<M>, key: K) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[key] as ComponentType };
  });
}

const Home = lazyRoute(() => import('../pages/Home'), 'Home');
const PublicHome = lazyRoute(() => import('../pages/PublicHome'), 'PublicHome');
const AboutLearn = lazyRoute(() => import('../pages/about'), 'AboutLearn');
const AboutUpdates = lazyRoute(() => import('../pages/about'), 'AboutUpdates');
const AboutRoadmap = lazyRoute(() => import('../pages/about'), 'AboutRoadmap');
const Download = lazyRoute(() => import('../pages/Download'), 'Download');
const Search = lazyRoute(() => import('../pages/Search'), 'Search');
const CreateSpace = lazyRoute(() => import('../pages/spaces'), 'CreateSpace');
const SpaceLayout = lazyRoute(() => import('../pages/spaces'), 'SpaceLayout');
const SpaceLanding = lazyRoute(() => import('../pages/spaces'), 'SpaceLanding');
const SpaceChannelView = lazyRoute(() => import('../pages/spaces'), 'SpaceChannelView');
const SpaceManageGate = lazyRoute(() => import('../pages/spaces'), 'SpaceManageGate');
const SpaceManageLayout = lazyRoute(() => import('../pages/spaces'), 'SpaceManageLayout');
const SpaceManageOverviewGate = lazyRoute(() => import('../pages/spaces'), 'SpaceManageOverviewGate');
const SpaceManageRoles = lazyRoute(() => import('../pages/spaces'), 'SpaceManageRoles');
const SpaceManageRoleDetail = lazyRoute(() => import('../pages/spaces'), 'SpaceManageRoleDetail');
const SpaceManageAuditLog = lazyRoute(() => import('../pages/spaces'), 'SpaceManageAuditLog');
const SpaceManageInvites = lazyRoute(() => import('../pages/spaces'), 'SpaceManageInvites');
const SpaceManageActivity = lazyRoute(() => import('../pages/spaces'), 'SpaceManageActivity');
const SpaceManageMembers = lazyRoute(() => import('../pages/spaces'), 'SpaceManageMembers');
const SpaceManageMemberDetail = lazyRoute(() => import('../pages/spaces'), 'SpaceManageMemberDetail');
const Login = lazyRoute(() => import('../pages/auth'), 'Login');
const Verify = lazyRoute(() => import('../pages/auth'), 'Verify');
const MfaVerify = lazyRoute(() => import('../pages/auth'), 'MfaVerify');
const ChooseInstance = lazyRoute(() => import('../pages/auth'), 'ChooseInstance');
const AddInstance = lazyRoute(() => import('../pages/auth'), 'AddInstance');
const AccountSecurity = lazyRoute(() => import('../pages/account'), 'AccountSecurity');
const AccountSubscription = lazyRoute(() => import('../pages/account'), 'AccountSubscription');
const ThemeBrowser = lazyRoute(() => import('../pages/account'), 'ThemeBrowser');
const ReferralPage = lazyRoute(() => import('../pages/account'), 'ReferralPage');
const AgeVerificationPage = lazyRoute(() => import('../pages/account'), 'AgeVerificationPage');
const CheckoutComplete = lazyRoute(() => import('../pages/checkout/CheckoutComplete'), 'CheckoutComplete');
const RequestSponsorshipPage = lazyRoute(() => import('../pages/sponsorship'), 'RequestSponsorshipPage');
const SponsorshipDirectoryPage = lazyRoute(() => import('../pages/sponsorship'), 'SponsorshipDirectoryPage');
const IdentityAppearance = lazyRoute(() => import('../pages/identity'), 'IdentityAppearance');
const IdentityCiphers = lazyRoute(() => import('../pages/identity'), 'IdentityCiphers');
const IdentityCustomEmojis = lazyRoute(() => import('../pages/identity'), 'IdentityCustomEmojis');
const IdentityDevices = lazyRoute(() => import('../pages/identity'), 'IdentityDevices');
const IdentityNotifications = lazyRoute(() => import('../pages/identity'), 'IdentityNotifications');
const IdentityAudioVideo = lazyRoute(() => import('../pages/identity'), 'IdentityAudioVideo');
const IdentityPrivacy = lazyRoute(() => import('../pages/identity'), 'IdentityPrivacy');
const IdentityProfile = lazyRoute(() => import('../pages/identity'), 'IdentityProfile');
const IdentityProfileView = lazyRoute(() => import('../pages/identity'), 'IdentityProfileView');
const ServiceStatus = lazyRoute(() => import('../pages/ServiceStatus'), 'ServiceStatus');
const ReferralLanding = lazyRoute(() => import('../pages/public/ReferralLanding'), 'ReferralLanding');
const SpaceInviteLanding = lazyRoute(
  () => import('../pages/public/SpaceInviteLanding'),
  'SpaceInviteLanding',
);
const ConversationView = lazyRoute(() => import('../pages/conversations/ConversationView'), 'ConversationView');
const NewConversation = lazyRoute(() => import('../pages/conversations/NewConversation'), 'NewConversation');
const AdminAuthAllowlist = lazyRoute(() => import('../pages/admin'), 'AdminAuthAllowlist');
const AdminAgeVerification = lazyRoute(() => import('../pages/admin'), 'AdminAgeVerification');
const AdminSpaces = lazyRoute(() => import('../pages/admin'), 'AdminSpaces');
const AdminDashboard = lazyRoute(() => import('../pages/admin'), 'AdminDashboard');
const AdminGate = lazyRoute(() => import('../pages/admin'), 'AdminGate');
const AdminLayout = lazyRoute(() => import('../pages/admin'), 'AdminLayout');
const AdminPlatformAdmins = lazyRoute(() => import('../pages/admin'), 'AdminPlatformAdmins');
const AdminUserSearch = lazyRoute(() => import('../pages/admin'), 'AdminUserSearch');
const AdminUserProfile = lazyRoute(() => import('../pages/admin'), 'AdminUserProfile');
const AdminIdentitySearch = lazyRoute(() => import('../pages/admin'), 'AdminIdentitySearch');
const AdminIdentityProfile = lazyRoute(() => import('../pages/admin'), 'AdminIdentityProfile');
const AdminPromoCodes = lazyRoute(() => import('../pages/admin'), 'AdminPromoCodes');
const AdminAnnouncements = lazyRoute(() => import('../pages/admin'), 'AdminAnnouncements');
const AdminMaintenance = lazyRoute(() => import('../pages/admin'), 'AdminMaintenance');
const ModeratorGate = lazyRoute(() => import('../pages/moderation'), 'ModeratorGate');
const ModeratorLayout = lazyRoute(() => import('../pages/moderation'), 'ModeratorLayout');
const ReportList = lazyRoute(() => import('../pages/moderation'), 'ReportList');
const ReportDetail = lazyRoute(() => import('../pages/moderation'), 'ReportDetail');
const TicketList = lazyRoute(() => import('../pages/moderation'), 'TicketList');
const ModerationTicketDetail = lazyRoute(() => import('../pages/moderation'), 'TicketDetail');
const MyTickets = lazyRoute(() => import('../pages/support'), 'MyTickets');
const SubmitTicket = lazyRoute(() => import('../pages/support'), 'SubmitTicket');
const TicketDetail = lazyRoute(() => import('../pages/support'), 'TicketDetail');
const FeedbackList = lazyRoute(() => import('../pages/feedback'), 'FeedbackList');
const FeedbackDetail = lazyRoute(() => import('../pages/feedback'), 'FeedbackDetail');
const SubmitFeedback = lazyRoute(() => import('../pages/feedback'), 'SubmitFeedback');
const LegalPoliciesPage = lazyRoute(() => import('../legal'), 'LegalPoliciesPage');
const LegalPolicyPage = lazyRoute(() => import('../legal'), 'LegalPolicyPage');
const ThirdPartyLicensesPage = lazyRoute(() => import('../legal'), 'ThirdPartyLicensesPage');

/** Fallback shown in the content area while a lazy route chunk loads. */
function RouteFallback() {
  return (
    <div className="route-loading">
      <div className="spinner spinner-lg" />
    </div>
  );
}

/**
 * Unified app shell that sits above both public and protected route groups.
 * Authenticated users get the full provider stack + sidebar once, so navigating
 * between public pages (Roadmap, Search) and protected pages (Conversations,
 * Account) no longer remounts providers or refetches data.
 * Unauthenticated users get a lightweight public sidebar with no provider overhead.
 */
function AuthenticatedShell() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="auth-layout">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (status === 'authenticated' || status === 'identity_mode') {
    return (
      <SiteAnnouncementsProvider>
        <TourProvider>
          <CipherStoreProvider>
            <ChatSocketProvider>
              <CaptchaGateProvider>
                <FriendsProvider>
                  <InboxProvider>
                  <BlockProvider>
                    <ConversationPreferencesProvider>
                      <SpacePreferencesProvider>
                        <ConversationFoldersProvider>
                          <ConversationsProvider>
                            <SpacesProvider>
                            <MediaOutboxProvider>
                            <CallSessionProvider>
                            <VoiceChannelSessionProvider>
                              <GlobalCallEventsProvider>
                                <AuthenticatedShellContent />
                              </GlobalCallEventsProvider>
                            </VoiceChannelSessionProvider>
                            </CallSessionProvider>
                            </MediaOutboxProvider>
                            </SpacesProvider>
                          </ConversationsProvider>
                        </ConversationFoldersProvider>
                      </SpacePreferencesProvider>
                    </ConversationPreferencesProvider>
                  </BlockProvider>
                  </InboxProvider>
                </FriendsProvider>
              </CaptchaGateProvider>
            </ChatSocketProvider>
          </CipherStoreProvider>
        </TourProvider>
      </SiteAnnouncementsProvider>
    );
  }

  // Public shell still mounts Spaces (and its ChatSocket/Cipher deps) because
  // `/search` (Spaces directory) and `/s/:slug` are public routes that call useSpaces / useCipherStore.
  return (
    <SiteAnnouncementsProvider>
      <CipherStoreProvider>
        <ChatSocketProvider>
          <SpacesProvider>
            <AppLayout sidebar={<AppSidebar variant="public" />}>
              <SiteAnnouncementBanner />
              <Suspense fallback={<RouteFallback />}>
                <Outlet />
              </Suspense>
            </AppLayout>
          </SpacesProvider>
        </ChatSocketProvider>
      </CipherStoreProvider>
    </SiteAnnouncementsProvider>
  );
}

/**
 * Inner layout for authenticated users. Has access to tour context and sets up
 * pre-key management, incoming call ringtones, and global overlays.
 */
function AuthenticatedShellContent() {
  const tour = useTourContext();
  const appearanceTour = useAppearanceTour();
  const { pathname } = useLocation();

  usePreKeys();
  useEnsureUpdateCheck();
  useIncomingCallRinger();

  return (
    <>
      <TourRoot tour={tour} />
      <TourRoot tour={appearanceTour} />
      <IdentityModalProvider>
        <ThreadPopoutProvider>
          <AppLayout sidebar={<AppSidebar />}>
            <KeyStorageBanner />
            <UpdateAvailableBanner hidden={pathname !== '/'} />
            <SiteAnnouncementBanner />
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </AppLayout>
        </ThreadPopoutProvider>
      </IdentityModalProvider>
      <AppCallOverlay />
      <VoiceChannelOverlay />
      <UpdateOverlay />
      <AchievementListener />
      <CallPresenceSoundListener />
      <SubscriptionChangeListener />
    </>
  );
}

/**
 * Thin guard for protected routes — redirects unauthenticated users to login.
 * Does not mount any providers or layout; the parent AuthenticatedShell handles that.
 */
function ProtectedGuard() {
  const { status } = useAuth();
  if (status === 'unauthenticated') {
    return <Navigate to="/auth/login" replace />;
  }
  return <Outlet />;
}

/**
 * Root path handler. Authenticated users see their Home dashboard;
 * unauthenticated visitors see the public landing page.
 */
function RootRedirect() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="auth-layout">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <PublicHome />;
  }

  return <Home />;
}

/**
 * Account routes (email/phone session, MFA, billing-adjacent controls) are not available
 * in an active alias context (identity session, unlocked alias, lock screen, or suspension)
 * — same rule as the account flyout in the sidebar.
 */
function AccountSessionOnlyOutlet() {
  const { status: authStatus } = useAuth();
  const { status: identityStatus } = useIdentity();
  if (isAccountSidebarHidden(authStatus, identityStatus)) {
    return <Navigate to="/identity/profile" replace />;
  }
  return <Outlet />;
}

/** Redirect legacy /account/security/:tab URLs to /account/:tab */
function SecurityTabRedirect() {
  const { tab } = useParams<{ tab: string }>();
  return <Navigate to={`/account/${tab ?? 'authentication'}`} replace />;
}

function CrashTestLevel1(): ReactNode {
  throw new Error('[CrashTest] Level 1: React render crash');
}

function CrashTestLevel2() {
  useEffect(() => {
    setTimeout(() => { throw new Error('[CrashTest] Level 2: uncaught error outside React'); }, 100);
  }, []);
  return (
    <p style={{ padding: '2rem', color: '#a1a1aa' }}>
      Level 2 fired. Check the Network tab for a <code>POST /api/client-errors</code> request.
      This layer reports silently — the app stays running.
    </p>
  );
}

function CrashTestLevel3() {
  useEffect(() => {
    setTimeout(() => { void Promise.reject(new Error('[CrashTest] Level 3: unhandled promise rejection')); }, 100);
  }, []);
  return (
    <p style={{ padding: '2rem', color: '#a1a1aa' }}>
      Level 3 fired. Check the Network tab for a <code>POST /api/client-errors</code> request.
      This layer reports silently — the app stays running.
    </p>
  );
}

/**
 * Main application component with all routes.
 * Shared across all platforms (web, desktop, mobile).
 */
export function App() {
  return (
    <UpdateProvider>
    <ToasterOutlet />
    <RouteErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Auth Routes */}
      <Route
        path="/auth/login"
        element={
          <AuthRoute>
            <Login />
          </AuthRoute>
        }
      />
      <Route
        path="/auth/verify"
        element={
          <AuthRoute>
            <Verify />
          </AuthRoute>
        }
      />
      <Route
        path="/auth/mfa"
        element={
          <AuthRoute>
            <MfaVerify />
          </AuthRoute>
        }
      />
      <Route
        path="/auth/instances"
        element={
          <InstanceRoute>
            <ChooseInstance />
          </InstanceRoute>
        }
      />
      <Route
        path="/auth/instances/add"
        element={
          <InstanceRoute>
            <AddInstance />
          </InstanceRoute>
        }
      />

      {/* Unified shell: single provider tree for authenticated users, public sidebar for guests */}
      <Route element={<AuthenticatedShell />}>

        {/* Public Routes (no auth required) */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/about/learn" element={<AboutLearn />} />
        <Route path="/about/roadmap" element={<AboutRoadmap />} />
        <Route path="/about/updates" element={<AboutUpdates />} />
        <Route path="/download" element={<Download />} />
        <Route path="/search" element={<Search />} />
        <Route path="/spaces" element={<Navigate to="/search?tab=spaces" replace />} />
        <Route path="/spaces/new" element={<CreateSpace />} />
        <Route path="/s/:slug" element={<SpaceLayout />}>
          <Route index element={<SpaceLanding />} />
          <Route path="c/:channelId" element={<SpaceChannelView />} />
          <Route path="manage" element={<SpaceManageGate />}>
            <Route element={<SpaceManageLayout />}>
              <Route index element={<SpaceManageOverviewGate />} />
              <Route path="members" element={<SpaceManageMembers />} />
              <Route path="members/:identityId" element={<SpaceManageMemberDetail />} />
              <Route path="roles" element={<SpaceManageRoles />} />
              <Route path="roles/:roleId" element={<SpaceManageRoleDetail />} />
              <Route path="roles/:roleId/:tab" element={<SpaceManageRoleDetail />} />
              <Route path="invites" element={<SpaceManageInvites />} />
              <Route path="activity" element={<SpaceManageActivity />} />
              <Route path="audit" element={<SpaceManageAuditLog />} />
            </Route>
          </Route>
        </Route>
        <Route path="/identity/:id" element={<IdentityProfileView />} />
        <Route path="/legal-policies" element={<LegalPoliciesPage />} />
        <Route path="/legal-policies/third-party" element={<ThirdPartyLicensesPage />} />
        <Route path="/legal-policies/:slug" element={<LegalPolicyPage />} />
        <Route path="/feedback" element={<FeedbackList />} />
        <Route path="/feedback/:postId" element={<FeedbackDetail />} />
        <Route path="/refer/:code" element={<ReferralLanding />} />
        {/* Public: the code is the capability, so this must not sit behind
            ProtectedGuard — the landing prompts for sign-in only when joining. */}
        <Route path={`${SPACE_INVITE_PATH_PREFIX}/:code`} element={<SpaceInviteLanding />} />
        <Route path="/service-status" element={<ServiceStatus />} />

        {/* Protected Routes (auth required — ProtectedGuard redirects guests to login) */}
        <Route element={<ProtectedGuard />}>

          {/* Account Routes (not available while alias session is unlocked) */}
          <Route element={<AccountSessionOnlyOutlet />}>
            <Route path="/account" element={<Navigate to="/account/overview" replace />} />
            <Route path="/account/security" element={<Navigate to="/account/authentication" replace />} />
            <Route path="/account/security/:tab" element={<SecurityTabRedirect />} />
            <Route path="/account/:tab" element={<AccountSecurity />} />
            <Route path="/account/subscription" element={<Navigate to="/account/subscription/manage" replace />} />
            <Route path="/account/subscription/:tab" element={<AccountSubscription />} />
            <Route path="/account/referrals" element={<ReferralPage />} />
            <Route path="/account/age-verification" element={<AgeVerificationPage />} />
            <Route path="/account/settings" element={<Navigate to="/identity/notifications" replace />} />
            <Route path="/account/appearance" element={<Navigate to="/identity/appearance" replace />} />
            <Route path="/account/appearance/community" element={<ThemeBrowser />} />
            <Route path="/sponsorship/request" element={<RequestSponsorshipPage />} />
            <Route path="/sponsorship/directory" element={<SponsorshipDirectoryPage />} />
          </Route>

          {/* Support tickets (account or identity session) */}
          <Route path="/support" element={<MyTickets />} />
          <Route path="/support/new" element={<SubmitTicket />} />
          <Route path="/support/:ticketId" element={<TicketDetail />} />

          {/* Community feedback — submit requires identity session */}
          <Route path="/feedback/new" element={<SubmitFeedback />} />

          {/* Identity Routes */}
          <Route path="/identity" element={<Navigate to="/identity/profile" replace />} />
          <Route path="/identity/profile" element={<IdentityProfile />} />
          {/* Longer static path before `/identity/appearance` so routers never prefer a dynamic match. */}
          <Route path="/identity/appearance/community" element={<ThemeBrowser />} />
          <Route path="/identity/appearance" element={<IdentityAppearance />} />
          <Route path="/identity/notifications" element={<IdentityNotifications />} />
          <Route path="/identity/audio-video" element={<IdentityAudioVideo />} />
          <Route path="/identity/privacy" element={<IdentityPrivacy />} />
          <Route path="/identity/devices" element={<IdentityDevices />} />
          <Route path="/identity/ciphers" element={<IdentityCiphers />} />
          <Route path="/identity/emojis" element={<IdentityCustomEmojis />} />
          <Route path="/identity/subscription" element={<Navigate to="/identity/subscription/manage" replace />} />
          <Route path="/identity/subscription/:tab" element={<AccountSubscription />} />

          {/* Conversation Routes */}
          <Route path="/conversations/new" element={<NewConversation />} />
          <Route path="/conversations/:id" element={<ConversationView />} />

          {/* Platform admin (nested layout + guard) */}
          <Route element={<AdminGate />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="platform-admins" element={<AdminPlatformAdmins />} />
              <Route path="auth-allowlist" element={<AdminAuthAllowlist />} />
              <Route path="age-verification" element={<AdminAgeVerification />} />
              <Route path="spaces" element={<AdminSpaces />} />
              <Route path="users" element={<AdminUserSearch />} />
              <Route path="users/:id" element={<AdminUserProfile />} />
              <Route path="identities" element={<AdminIdentitySearch />} />
              <Route path="identities/:id" element={<AdminIdentityProfile />} />
              <Route path="promo-codes" element={<AdminPromoCodes />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
            </Route>
          </Route>

          {/* Platform moderation (moderator + admin guard) */}
          <Route element={<ModeratorGate />}>
            <Route path="/moderation" element={<ModeratorLayout />}>
              <Route index element={<Navigate to="tickets" replace />} />
              <Route path="reports" element={<ReportList />} />
              <Route path="reports/:id" element={<ReportDetail />} />
              <Route path="tickets" element={<TicketList />} />
              <Route path="tickets/:id" element={<ModerationTicketDetail />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Utility Routes (no auth required, no shell layout) */}
      <Route path="/checkout/complete" element={<CheckoutComplete />} />

      {/* Dev-only crash test routes */}
      {import.meta.env.DEV && (
        <>
          <Route path="/level1" element={<CrashTestLevel1 />} />
          <Route path="/level2" element={<CrashTestLevel2 />} />
          <Route path="/level3" element={<CrashTestLevel3 />} />
        </>
      )}

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
    </UpdateProvider>
  );
}
